// B2B authentication HTTP layer.
// Pure functions: no store access, no side effects — orchestration lives in
// the user Vuex module and the watcher.
import axios from 'axios';

const AUTHENTICATE_PATH = '/b2b/authenticate';
const VALIDATION_PATH   = '/user-account/signup/auth/validation';
const REFRESH_PATH      = '/b2b/users/token';

// Hard cap: no B2B request should hang past this. The watcher runs every
// minute, so anything longer than ~10s would start stacking calls.
const REQUEST_TIMEOUT_MS = 10000;

function baseUrl() {
    return process.env.VUE_APP_BET_API_URL;
}

// POST {proxyUrl}/b2b/authenticate with the partner's token. The proxy calls rocky-bets /b2b/authenticate
// (partner check, user registration, b2bToken saved for the /b2b/ routes), then logs the user into
// Fusio, and answers like the skin login: {token, refresh_token, expires_in, userData: <rocky-bets user>}.
// Public operation: no Authorization header (Fusio checks any it gets); customer and platform come from
// the proxy host. → { userId, profileType, userData, token, refreshToken, expiresIn }
export async function b2bAuthenticate({ proxyUrl, operatorToken }) {
    if (!proxyUrl) {
        throw new Error('B2B authenticate needs the proxy url of the skin');
    }
    const { data } = await axios.post(
        `${proxyUrl}${AUTHENTICATE_PATH}`,
        { token: operatorToken },
        { timeout: REQUEST_TIMEOUT_MS }
    );
    const user = (data && data.userData) || {};
    return {
        userId:       user.userId,
        profileType:  user.profileType != null ? user.profileType : null,
        userData:     user,
        token:        (data && data.token) || null,
        refreshToken: (data && data.refresh_token) || null,
        expiresIn:    data && data.expires_in != null ? data.expires_in : null
    };
}

// GET {proxyUrl}/user-account/signup/auth/validation?refresh=… with the proxy token, as the skins do:
// { success, refresh, userToken? }. A token no longer valid gets a 401 from the proxy.
export async function b2bValidateSession({ proxyUrl, accessToken, refreshToken }) {
    const { data } = await axios.get(
        `${proxyUrl}${VALIDATION_PATH}?refresh=${encodeURIComponent(refreshToken || '')}`,
        { headers: { Authorization: `Bearer ${accessToken}` }, timeout: REQUEST_TIMEOUT_MS }
    );
    return data;
}

// POST /b2b/users/token → { token, ... }
export async function b2bRefreshToken({ operatorToken, customerId, platformId }) {
    const { data } = await axios.post(
        `${baseUrl()}${REFRESH_PATH}`,
        { token: operatorToken, customerId, platformId },
        { timeout: REQUEST_TIMEOUT_MS }
    );
    return data;
}

// True when the axios error is a network hiccup (no response, timeout, 5xx, or
// 429 too many requests) as opposed to an explicit backend rejection with a
// business code. We treat these as "transient": the watcher retries a few times,
// further and further apart, before logging out.
export function isTransientAxiosError(err) {
    if (!err) return false;
    if (err.code === 'ECONNABORTED') return true; // timeout
    if (!err.response) return true;                // no response at all
    const status = err.response.status;
    if (status === 429) return true;
    if (typeof status === 'number' && status >= 500) return true;
    return false;
}

// The business code of a failed B2B call: `code` (partner) or `error_code` (rocky-bets AppError, passed
// through by the proxy), as a number when it is one. 0 for a transient failure without a code, 500 otherwise.
export function b2bErrorCode(err, transient) {
    const data = err && err.response && err.response.data;
    const raw = data ? (data.code != null ? data.code : data.error_code) : null;
    if (raw != null && raw !== '') {
        const numeric = Number(raw);
        return Number.isNaN(numeric) ? raw : numeric;
    }
    return transient ? 0 : 500;
}

// Backend convention: numeric error code 6002 means "expired B2B token".
export const B2B_ERROR_TOKEN_EXPIRED = 6002;
export const B2B_ERROR_NO_TOKEN      = 6001;
